import { useMemo } from 'react'

import ForumIcon from '@mui/icons-material/Forum'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import HomeIcon from '@mui/icons-material/Home'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import PersonIcon from '@mui/icons-material/Person'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'

import useTotalUnreadMessageCount from '@shared/hooks/src/useTotalUnreadMessageCount'

import useFormatMessage from '@hooks/useFormatMessage'
import { useProfileMissingErrors } from '@pages/Account/Account.hooks'
import { useHomeTodos } from '@pages/Home/Home.hooks'

/** Routes that are a destination in their own right rather than somewhere you drilled into. */
const MAIN_ROUTES = ['/app', '/app/health', '/app/messages', '/app/account']

export const NavId = {
  Home: 'nav-home',
  Health: 'nav-health',
  Messages: 'nav-messages',
  Profile: 'nav-profile',
}

const normalize = (pathname) => (pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname)

/**
 * The app's primary destinations, with their live badge counts.
 *
 * Both shells read from here, so the bottom tab bar and the desktop side navigation
 * cannot drift apart in labels, order, icons or counts.
 *
 * @returns {Array<{id: string, to: string, label: string, a11yLabel: string, Icon: React.ElementType, IconActive: React.ElementType, count: number, badgeColor?: string}>}
 *   The primary navigation destinations.
 */
export function useNavigationItems() {
  const formatMessage = useFormatMessage()

  const { count: todoCount } = useHomeTodos()
  const { data: profileErrors } = useProfileMissingErrors()
  const unreadCount = useTotalUnreadMessageCount()

  const profileErrorCount = useMemo(() => Object.values(profileErrors || {}).reduce((sum, count) => sum + count, 0), [profileErrors])

  return useMemo(() => {
    const items = [
      {
        id: NavId.Home,
        to: '/app',
        label: formatMessage('navigation.home'),
        Icon: HomeOutlinedIcon,
        IconActive: HomeIcon,
        count: todoCount,
        badgeColor: 'warning',
      },
      {
        id: NavId.Health,
        to: '/app/health',
        label: formatMessage('navigation.health'),
        Icon: MonitorHeartOutlinedIcon,
        IconActive: MonitorHeartIcon,
        count: 0,
      },
      {
        id: NavId.Messages,
        to: '/app/messages',
        label: formatMessage('navigation.chat'),
        Icon: ForumOutlinedIcon,
        IconActive: ForumIcon,
        count: unreadCount || 0,
        badgeColor: 'error',
      },
      {
        id: NavId.Profile,
        to: '/app/account',
        label: formatMessage('navigation.profile'),
        Icon: PersonOutlinedIcon,
        IconActive: PersonIcon,
        count: profileErrorCount,
        badgeColor: 'warning',
      },
    ]

    return items.map((item) => ({
      ...item,
      a11yLabel: item.count > 0 ? formatMessage('navigation.tabs.a11y.badge', { label: item.label, count: item.count }) : item.label,
    }))
  }, [formatMessage, todoCount, unreadCount, profileErrorCount])
}

/**
 * Whether a destination owns the current location. Home only matches exactly, since
 * every other route is nested under `/app`.
 *
 * @param {string} pathname - The current location pathname.
 * @param {string} to - The destination path.
 * @returns {boolean} True when the destination should read as active.
 */
export function isNavItemActive(pathname, to) {
  const path = normalize(pathname)

  if (to === '/app') return path === '/app'

  return path === to || path.startsWith(`${to}/`)
}

/**
 * Whether the current route is one of the primary destinations.
 *
 * @param {string} pathname - The current location pathname.
 * @returns {boolean} True on a main route.
 */
export function isMainRoute(pathname) {
  return MAIN_ROUTES.includes(normalize(pathname))
}
